import { Formik, Form, Field } from "formik";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import FieldErrorMessage from "@/Components/common/FieldErrorMessage";
import { RequiredSymbol } from "@/Components/common/RequiredSymbol";
import { updateShowBoxes } from "@/reducers/auth";
import { _verifyCode, _forgotPassword } from "@/services/auth";
import { handleAPICatch, showSuccessToast } from "@/utilities/utils";
import { validateVerifyCode } from "@/validations/auth/login";

import { setSession } from "../../../reducers/session";
import AuthButton from "../AuthButton";
import LoginFormTopContent from "../LoginFormTopContent";

/**
 * Function to resend verification code.
 *
 * @param {void}
 * @return {void}
 */
const VerificationCodeForm = ({ action }) => {
  const dispatch = useDispatch();
  const { new_user } = useSelector((state) => state.auth);
  const [resentVerificationCode, setResentVerificationCode] = useState(false);
  const [reSending, setReSending] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Function to resend verification code.
   */
  const resendVerificationCode = () => {
    const data = {
      user_id: new_user.id,
      user_type: new_user.type,
      email: new_user.email,
    };
    let response = _forgotPassword({
      email: new_user.email,
    });
    response
      .then((res) => {
        let {
          status: resStatus,
          data: { user, message },
        } = res;
        if (resStatus == 200) {
          setResentVerificationCode(true);
        }
        showSuccessToast(message);
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        setReSending(false);
      });
  };

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    setSubmitting(true);
    values = {
      ...values,
      user_type: new_user?.type,
      user_email: new_user?.email,
    };
    let response = _verifyCode(values);
    response
      .then((res) => {
        let {
          status: resStatus,
          data: { message: resMsg, user },
        } = res;
        let { user_id, user_type, id, client_id } = user;

        if (values.action == "ResetPassword") {
          if (user.user_type == "ClientUser") {
            dispatch(
              setSession({ reset_user_id: id, reset_user_type: user_type }),
            );
          } else {
            dispatch(
              setSession({
                reset_user_id: client_id,
                reset_user_type: user_type,
              }),
            );
          }
        }

        if (resStatus == 200) {
          dispatch(updateShowBoxes("set_new_password"));
          showSuccessToast(resMsg);
          resetForm();
        } else {
          showSuccessToast(resMsg);
        }
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        setSubmitting(false);
        setIsSubmitting(false);
      });
  };
  return (
    <div className="auth_form_wrapper">
      <Formik
        initialValues={{
          verify_code: "",
          user_type: null,
          user_email: null,
          action: action === "ResetPassword" ? "ResetPassword" : "Register",
        }}
        validateOnChange={false}
        validateOnBlur={false}
        validateOnMount={false}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          handleSubmit(values, { setSubmitting, resetForm });
        }}
      >
        {({
          submitForm,
          isSubmitting,
          errors,
          touched,
          isValid,
          setSubmitting,
        }) => (
          <Form className="flex flex-col gap-6">
            <LoginFormTopContent
              title="Verification Code"
              desc="A verification email has been sent to your email address. Please copy the code into the box below to re-set your password."
            />
            <div>
              <div className="flex flex-col gap-3">
                <label htmlFor="verify_code" className="label">
                  Enter Verification Code <RequiredSymbol />
                </label>
                <div>
                  <Field
                    type="text"
                    className={`h-[40px] w-full rounded-lg border border-bodyText bg-none text-black shadow-none placeholder:text-[14px] placeholder:font-medium placeholder:text-txtColor autofill:bg-none autofill:shadow-none focus:border focus:border-primary focus:shadow-none focus:ring-0 ${
                      errors.verify_code && touched.verify_code
                        ? "!border-red-500"
                        : ""
                    }`}
                    autoComplete="off"
                    name="verify_code"
                    placeholder="Enter Verification Code"
                    validate={(value) => validateVerifyCode(value)}
                    onKeyUp={(e) => {
                      if (touched.verify_code && errors.verify_code) {
                        validateForm();
                      }
                      if (e.key === "Enter") {
                        submitForm();
                        if (document.activeElement != document.body)
                          document.activeElement.blur();
                      }
                    }}
                  />
                  <FieldErrorMessage name="verify_code" />
                </div>
              </div>
              <div className="mt-2 text-right">
                {resentVerificationCode ? (
                  <p className="font-medium">
                    {" "}
                    Verification code sent to your email address
                  </p>
                ) : (
                  <span
                    className="authLink inline-block !text-[14px] !text-blue"
                    onClick={() => {
                      if (reSending) return;
                      setReSending(true);
                      resendVerificationCode();
                    }}
                  >
                    {reSending
                      ? "Resending Verification Code "
                      : "Resend Verification Code"}
                  </span>
                )}
              </div>
              <AuthButton
                submitForm={submitForm}
                isSubmitting={isSubmitting}
                isValid={isValid}
                btnText={"Verify"}
                btnLoadingText={"Verifying"}
                disabled={isSubmitting}
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default VerificationCodeForm;
